import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'
import { type IconName } from '@/icon-name'

const tabVariants = cva(
	// Base styles - Layout, typography, and transitions
	'inline-flex items-center justify-center gap-2 transition-all duration-200 ease-in-out outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none',
	{
		variants: {
			state: {
				default: [
					// Default state - Design specs: Text blue-800, no background/border
					'bg-transparent text-blue-800',
					// Always have consistent border width to prevent layout shift
					'border-[1.5px] border-transparent',
					// Hover state - Design specs: Border blue-100 1px - using same width for consistency
					'hover:border-blue-100 hover:bg-transparent',
					// Focus state for accessibility
					'focus-visible:border-blue-100 focus-visible:bg-transparent',
				],
				selected: [
					// Selected state - Design specs: Background #E7F6FD (blue-50), Border #0CA1E9 (blue-500) 1.5px, Text #0CA1E9 (blue-500)
					'border-blue-500 bg-blue-50 text-blue-500',
					'border-[1.5px]',
				],
				disabled: [
					// Disabled state - Design specs: Border #F0F0F0, Text #8C8C8C
					'border-neutral-200 bg-transparent text-neutral-500',
					'cursor-not-allowed border-[1.5px]',
				],
			},
			variant: {
				primary: '',
				secondary: '',
			},
			size: {
				default: [
					// Design specs: 16px left/right, 8px top/bottom padding
					'h-[46px] px-4 py-2',
					// Design specs: Parkinsans/18px-Regular
					'font-parkinsans text-[18px] leading-[30px]',
					// Design specs: Border radius 64px
					'rounded-[64px]',
				],
				mini: [
					// Mini variant - Design specs: 4px padding
					'p-1',
					// Mini variant - Design specs: Parkinsans/14px-Medium for selected, Parkinsans/14px-Regular for default
					'font-parkinsans text-sm',
					// Mini variant - Design specs: Border radius 64px (same as default)
					'rounded-full',
				],
			},
		},
		compoundVariants: [
			// Font weight based on design specs
			{
				state: 'default',
				className: 'font-normal',
			},
			{
				state: 'selected',
				className: 'font-medium',
			},
			{
				state: 'disabled',
				className: 'font-normal',
			},
			// Secondary variant colors
			{
				variant: 'secondary',
				state: 'default',
				className: [
					'text-green-800',
					'hover:border-green-100 hover:bg-transparent',
					'focus-visible:border-green-100 focus-visible:bg-transparent',
				],
			},
			{
				variant: 'secondary',
				state: 'selected',
				className: ['border-green-500 bg-green-50 text-green-500'],
			},
			// Mini variant specific styling
			{
				size: 'mini',
				state: 'selected',
				className: [
					// Mini selected - Design specs: bg-blue-600, text-neutral-50
					'border-blue-600 bg-blue-600 text-neutral-50',
					'border-[1.5px]',
					'leading-snug',
				],
			},
			{
				size: 'mini',
				state: 'default',
				className: [
					// Mini default - Design specs: bg-white, border-blue-50, text-blue-800
					'border-blue-50 bg-white text-blue-800',
					'border-[1.5px]',
					'leading-snug',
					'hover:border-blue-100 hover:bg-white',
				],
			},
			{
				size: 'mini',
				state: 'disabled',
				className: [
					// Mini disabled - maintaining consistency with default disabled
					'border-neutral-200 bg-white text-neutral-500',
					'border-[1.5px]',
					'leading-snug',
				],
			},
		],
		defaultVariants: {
			state: 'default',
			variant: 'primary',
			size: 'default',
		},
	},
)

export interface TabProps
	extends Omit<React.ComponentProps<'button'>, 'disabled' | 'onClick'>,
		VariantProps<typeof tabVariants> {
	/**
	 * If set to `true`, the button will be rendered as a child within the component.
	 * This child component must be a valid React component.
	 */
	asChild?: boolean
	/**
	 * Tab state - controls visual appearance and behavior
	 */
	state?: 'default' | 'selected' | 'disabled'
	/**
	 * Color variant - controls tab color scheme
	 */
	variant?: 'primary' | 'secondary'
	/**
	 * Size variant - controls tab dimensions and styling
	 */
	size?: 'default' | 'mini'
	/**
	 * Icon to display in the tab (optional)
	 */
	icon?: IconName
	/**
	 * Icon position - left or right side of the text
	 */
	iconPosition?: 'left' | 'right'
	/**
	 * Whether the tab is disabled
	 */
	disabled?: boolean
	/**
	 * Unique identifier for the tab
	 */
	id?: string
	/**
	 * Callback when tab is clicked
	 */
	onClick?: (id?: string) => void
	/**
	 * Standard button click handler
	 */
	onButtonClick?: React.MouseEventHandler<HTMLButtonElement>
}

const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
	(
		{
			className,
			state = 'default',
			variant = 'primary',
			size = 'default',
			asChild = false,
			icon,
			iconPosition = 'left',
			disabled = false,
			children,
			id,
			onClick,
			onButtonClick,
			...props
		},
		ref,
	) => {
		// Determine the actual state based on disabled prop
		const actualState = disabled ? 'disabled' : state

		const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			if (disabled) return

			if (onClick) {
				onClick(id)
			}

			// Call standard button click handler if provided
			if (onButtonClick) {
				onButtonClick(e)
			}
		}

		const Comp = asChild ? Slot : 'button'

		// Icon element with size based on tab size variant
		const iconSize = size === 'mini' ? 'xs' : 'sm'
		const iconElement = icon && (
			<Icon
				name={icon}
				size={iconSize}
				className={cn(
					'flex-shrink-0',
					// Mini variant icon styling to match Figma specs
					size === 'mini' &&
						actualState === 'selected' &&
						'rounded-xl bg-blue-400 p-2',
					size === 'mini' &&
						actualState === 'default' &&
						'rounded-xl bg-blue-50 p-2',
				)}
				aria-hidden="true"
			/>
		)

		// With asChild, we can't add our own content structure
		if (asChild) {
			return (
				<Comp
					ref={ref}
					className={cn(
						tabVariants({ state: actualState, variant, size }),
						className,
					)}
					disabled={disabled}
					role="tab"
					aria-selected={actualState === 'selected'}
					aria-disabled={disabled}
					tabIndex={disabled ? -1 : 0}
					{...props}
					onClick={handleClick}
				>
					{children}
				</Comp>
			)
		}

		return (
			<Comp
				ref={ref}
				type="button"
				className={cn(
					tabVariants({ state: actualState, variant, size }),
					className,
				)}
				disabled={disabled}
				role="tab"
				aria-selected={actualState === 'selected'}
				aria-disabled={disabled}
				tabIndex={disabled ? -1 : 0}
				{...props}
				onClick={handleClick}
			>
				{/* Icon on the left */}
				{icon && iconPosition === 'left' && iconElement}

				{/* Tab content */}
				{children}

				{/* Icon on the right */}
				{icon && iconPosition === 'right' && iconElement}
			</Comp>
		)
	},
)

Tab.displayName = 'Tab'

// TabGroup component for managing multiple tabs
export interface TabGroupProps extends React.ComponentProps<'div'> {
	/**
	 * Currently selected tab ID
	 */
	selectedTab?: string
	/**
	 * Callback when a tab is selected
	 */
	onTabChange?: (tabId: string) => void
	/**
	 * Tab orientation
	 */
	orientation?: 'horizontal' | 'vertical'
	/**
	 * Alignment of tabs within the group
	 */
	alignment?: 'start' | 'center' | 'end'
	/**
	 * Size variant for all tabs in the group
	 */
	size?: 'default' | 'mini'
}

const TabGroup = React.forwardRef<HTMLDivElement, TabGroupProps>(
	(
		{
			className,
			selectedTab,
			onTabChange,
			orientation = 'horizontal',
			alignment = 'center',
			size = 'default',
			children,
			...props
		},
		ref,
	) => {
		const handleTabClick = (tabId?: string) => {
			if (tabId && onTabChange) {
				onTabChange(tabId)
			}
		}

		// Clone children and inject props
		const enhancedChildren = React.Children.map(children, (child) => {
			if (React.isValidElement(child) && child.type === Tab) {
				const tabProps = child.props as TabProps
				return React.cloneElement(child as React.ReactElement<TabProps>, {
					state: tabProps.id === selectedTab ? 'selected' : 'default',
					size: size, // Pass size prop to all tabs
					onClick: handleTabClick,
					// Preserve the variant prop from the original tab
					variant: tabProps.variant,
				})
			}
			return child
		})

		return (
			<div
				ref={ref}
				className={cn(
					// Base layout styles
					'inline-flex items-center',
					// Conditional styling based on size variant
					size === 'mini'
						? [
								// Mini variant - Design specs: 4px gap, 4px padding, blue-50 background, 32px border radius
								'gap-1 rounded-[32px] bg-blue-50 p-1',
							]
						: [
								// Default variant - Design specs: 8px spacing, 4px padding, rounded container
								'gap-2 rounded-[100px] p-1',
							],
					// Orientation
					orientation === 'vertical' && 'flex-col',
					orientation === 'horizontal' && 'flex-row',
					// Alignment
					alignment === 'start' && 'justify-start',
					alignment === 'center' && 'justify-center',
					alignment === 'end' && 'justify-end',
					className,
				)}
				role="tablist"
				aria-orientation={orientation}
				{...props}
			>
				{enhancedChildren}
			</div>
		)
	},
)

TabGroup.displayName = 'TabGroup'

export { Tab, TabGroup, tabVariants }
